import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import store from 'store/Store';
import { mediaQueries, space } from 'style/config';

import { Box, Text, Divider } from 'components/ui';
import BreadCrumbs from 'components/Catalog/BreadCrumbs';
import CatalogLayout from 'components/Catalog/CatalogLayout';
import RegularFilters from 'components/Catalog/Filters';
import { withUpperFunnelProps } from 'viewModel/catalog/upperFunnel/withUpperFunnelProps';
const Filters = withUpperFunnelProps(RegularFilters);
import Categories from 'components/Catalog/Categories';
import RegularProductGrid from 'components/Catalog/ProductGrid';
const ProductGrid = withUpperFunnelProps(RegularProductGrid);
import SeoText from 'components/Catalog/SeoText';
import SoftLinks from 'components/Catalog/SoftLinks';
import RelatedContent from 'components/RelatedContent';
import mediaUtils from 'utils/Media';
import { PAGE_TYPES } from '@ufe/shared/constants/catalog';
import catalogUtils, { BP_VALID_CATEGORY_IDS, CAROUSEL_ELIGIBLE_ROOT_CATEGORY_IDS } from 'utils/Catalog';
import CatalogPageBindings, { prepareDeferredZipFilterAnalytics } from 'analytics/bindingMethods/pages/catalog/catalogPageBindings';
import { ZIP_CODE_REFINEMENTS } from 'constants/UpperFunnel';
import pixleeUtils from 'utils/pixlee';
import LazyLoad from 'components/LazyLoad';
import localeUtils from 'utils/LanguageLocale';
import Copy from 'components/Content/Copy';
import userUtils from 'utils/User';
import { CLICK_TRACKER_ID_FIELD } from 'constants/sponsoredProducts';
import RMN_BANNER_TYPES from 'components/Rmn/constants';
import spaUtils from 'utils/Spa';
import RmnAndPlaUtils from 'utils/rmnAndPla';
import { fireRoktPageView } from 'utils/adUtils';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import PixleeContainer from 'components/Catalog/PixleeContainer/PixleeContainer';
import nthCategoryBindings from 'analytics/bindingMethods/pages/nthCategory/NthCategoryBindings';
import { PostLoad } from 'constants/events';
import SkeletonBanner from 'components/Banner/SkeletonBanner/SkeletonBanner';
import contentConstants from 'constants/content';
import BeautyPreferenceSpoke from 'components/BeautyPreferenceSpoke';
import { getMatchReferer } from 'analytics/utils/cmsReferer';
import ComponentResponsiveChangeCheck from 'utils/ComponentResponsiveChangeCheck';
import Empty from 'constants/empty';
import bpRedesignedUtils from 'utils/BeautyPreferencesRedesigned.js';

import RecServiceCarousel from 'components/RecServiceCarousel';
import { CONSTRUCTOR_PODS, GROUPING } from 'constants/constructorConstants';
import RmnLeaderboardBanner from 'components/Rmn/RmnLeaderboardBanner';
import RmnSiderailBanner from 'components/Rmn/RmnSiderailBanner';
import RmnBottomBanner from 'components/Rmn/RmnBottomBanner';
import FAQCollection from 'components/Content/FAQCollection';
import { mountBannerEventData } from 'analytics/utils/eventName';
import anaConsts from 'analytics/constants';
import deepExtend from 'utils/deepExtend';
import isRmnCombinedCallFeatureEnabled from 'components/Rmn/utils';
import CatalogEN from 'components/Catalog/locales/Catalog_en_US';
import CatalogFR from 'components/Catalog/locales/Catalog_fr_CA';
import FreeShippingBanner from 'components/Banner/FreeShippingBanner';
const getCatalogText = localeUtils.getLocalisedDataFromResource(CatalogEN, CatalogFR);

const { FILTERS_SIDEBAR_WIDTH } = contentConstants;
const { Media } = mediaUtils;
const { loadPixlee } = pixleeUtils;

const COLLAPSED_ACCORDION_HEIGHT = 50;
const DEFAULT_SIDEBAR_HEIGHT = 1000;
const EXTRA_HEIGHT_WHEN_ALL_FILTERS_SHOWN = 200;

const {
    POSITIONS, MOBILE_VIEW, SECTIONS, TYPES, LEADERBOARD_POSITION_INDEX, BANNER_AND_PLA_TYPES
} = RMN_BANNER_TYPES;
const BANNER_POSITIONS = POSITIONS;

const RenderSidebar = memo(({
    catalog, pageType, categoriesProps, filters, isMobile, rmnBannerPayload, slotPrefix
}) => {
    // If no RMN banner, don't render the banner component
    const hasRmnBanner = !!rmnBannerPayload && Object.keys(rmnBannerPayload).length > 0;

    const slot = rmnBannerPayload?.slot || `${slotPrefix}${TYPES.WIDE_SIDESCRAPER.SLOT[RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY]}`;

    const sideBannerProps = {
        slot,
        section: SECTIONS.SIDEBAR,
        type: TYPES.WIDE_SIDESCRAPER.NAME,
        position: POSITIONS.TOP,
        source: RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY,
        clickEventName: mountBannerEventData({
            section: SECTIONS.SIDEBAR,
            targetPage: anaConsts.RMN_PAGE_NAMES.category,
            type: anaConsts.EVENTS_TYPES_NAME.CLICK
        }),
        viewableEventName: mountBannerEventData({
            section: SECTIONS.SIDEBAR,
            targetPage: anaConsts.RMN_PAGE_NAMES.category,
            type: anaConsts.EVENTS_TYPES_NAME.VIEW
        })
    };

    return (
        <>
            <Text
                is='div'
                fontSize={['lg', 'xl']}
                lineHeight='tight'
                marginBottom={[null, null, '1em']}
                fontWeight='bold'
                css={{ order: -1 }}
            >
                {catalog.displayName}
            </Text>
            {pageType === PAGE_TYPES.NTH_CATEGORY_PAGE ? (
                <>
                    {filters}
                    {!isMobile && (
                        <Categories
                            {...categoriesProps}
                            showHeading={true}
                        />
                    )}
                </>
            ) : (
                <>
                    <Categories {...categoriesProps} />
                    {filters}
                </>
            )}
            {isRmnCombinedCallFeatureEnabled() && hasRmnBanner ? (
                <RmnSiderailBanner
                    pageType={RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY}
                    rmnBannerPayload={rmnBannerPayload}
                    bannerProps={sideBannerProps}
                />
            ) : null}
        </>
    );
});

// GUAR-8295: Decide between firing immediately, deferring (for combined fire from ProductGrid),
// or suppressing entirely when the initial PAGE_LOAD was gated for EDD (set in
// NthCategoryPage.componentDidMount) and this isn't a user-initiated filter selection.
// Extracted from getDerivedStateFromProps to keep complexity in check.
function handleZipFilterAnalytics({ isZipFilter, trackFilterSelection, catalog, categoryFilters }) {
    // GUAR-8371: Clear any stale deferred payload from a prior zip-filter change where
    // deliveryDate never arrived. Without this, ProductGrid's EDD useEffect could later
    // fire a combined ASYNC_PAGE_LOAD in an out-of-date context (wrong filters/pageName).
    // The deferred branch below re-establishes pendingZipFilterAnalytics if applicable.
    delete digitalData.page.attributes.pendingZipFilterAnalytics;

    if (!isZipFilter) {
        CatalogPageBindings.fireAsyncPageLoadAnalytics(catalog, categoryFilters, trackFilterSelection);

        return;
    }

    // plpEddGated is set synchronously in componentDidMount when we push the gate
    // to initialLoadDependencies, so it's reliable regardless of PAGE_LOAD bindings timing.
    const gatedForEdd = !!digitalData.page.attributes.plpEddGated;
    const shouldSuppressDefer = !trackFilterSelection && gatedForEdd;

    if (shouldSuppressDefer) {
        delete digitalData.page.attributes.plpEddGated;

        return;
    }

    prepareDeferredZipFilterAnalytics(catalog, categoryFilters, trackFilterSelection);
}

class NthCategoryPage extends BaseClass {
    constructor(props) {
        super(props);
        const { isPaginationFirstPage = false } = props;

        this.state = {
            prevContextId: null,
            selectedFilters: {},
            appliedFilters: {},
            isPaginationFirstPage,
            bannerLoading: true,
            isBeautyPreferencesSpokeDisabled: true,
            showUpdateDefaultSort: props.showUpdateDefaultSort,
            rmnSideRailBannerPayload: {},
            rmnLeaderboardBannerPayload: {},
            rmnMidPageBannerPayload: {},
            rmnBottomBannerPayload: {},
            fallbackBannerPayload: {},
            initialFiltersSnapshot: null,
            trackFilterSelection: false
        };

        this.responsiveCheck = null;

        if (Sephora.Util?.InflatorComps?.services?.CatalogService) {
            Sephora.Util.InflatorComps.services.CatalogService.catalogEngine = props.catalog?.responseSource;
        }

        this.layoutWrapRef = React.createRef();
    }

    static getDerivedStateFromProps({ catalog, showUpdateDefaultSort, userInfo }, state) {
        const pendingClientSideRender = catalog?.hasClientSideData && !state.hasClientSideData;

        // Determine if BP enrichment data has changed since last derivation
        const parentCategoryId = catalog?.categories?.[0]?.categoryId;
        const isBpValidCategory = BP_VALID_CATEGORY_IDS.includes(parentCategoryId);
        const categorySpecificMasterList =
            !Sephora.isNodeRender && isBpValidCategory ? bpRedesignedUtils.getBeautyPreferencesWorldPageInfo(undefined, parentCategoryId) : null;
        const pendingBpEnrichment = categorySpecificMasterList && !state.categorySpecificMasterList;
        const customerPreference = userInfo?.customerPreference;
        const customerPreferenceChanged = JSON.stringify(customerPreference) !== JSON.stringify(state.prevCustomerPreference);

        if (catalog.contextId === state.prevContextId && !pendingClientSideRender && !pendingBpEnrichment && !customerPreferenceChanged) {
            return null;
        }

        const contextChanged = catalog.contextId !== state.prevContextId;

        // Enrich BP refinement values before computing filters so selectedFilters includes BP selections
        const enrichedCatalog =
            categorySpecificMasterList && catalog.products?.length > 0
                ? {
                    ...catalog,
                    refinements: catalog.refinements?.map(refinement => {
                        if (refinement.type === 'beautyPreferences') {
                            return {
                                ...refinement,
                                values: catalogUtils.getBeautyPreferencesRefinementValues({
                                    userSavedBeautyPreferences: customerPreference,
                                    constructorRefinements: catalog.refinements,
                                    categorySpecificMasterList
                                })
                            };
                        }

                        return refinement;
                    })
                }
                : catalog;

        const pageId = catalog.categoryId;
        const isSamePage = pageId === state.prevPageId;
        const result = catalogUtils.createFiltersWithSortRefinements(enrichedCatalog, state.selectedFilters, isSamePage, showUpdateDefaultSort);
        const fireAnalytics = state.prevContextId !== null && !pendingClientSideRender && contextChanged; // only fire on context changes

        if (fireAnalytics) {
            if (isSamePage) {
                if (state.suppressNextAsyncPageLoad) {
                    // Modal-originated filter apply handles its own analytics — skip to avoid duplicate ASYNC_PAGE_LOAD.
                } else {
                    // GUAR-8295: Detect zip-code-based filters (SameDay/ShipToHome).
                    // These trigger a product refetch that brings EDD data asynchronously.
                    // Defer the ASYNC_PAGE_LOAD so ProductGrid can fire a combined call
                    // that includes both filter events and event320/eVar141.
                    const appliedFilterValues = Object.values(result.filters).flat();
                    const isZipFilter = ZIP_CODE_REFINEMENTS.some(key =>
                        appliedFilterValues.some(val => typeof val === 'string' && val.startsWith(key))
                    );

                    handleZipFilterAnalytics({
                        isZipFilter,
                        trackFilterSelection: state.trackFilterSelection,
                        catalog,
                        categoryFilters: result.categoryFilters
                    });
                }
            } else {
                CatalogPageBindings.setPageLoadAnalytics(catalog, result.categoryFilters); // spa navigation analytics
            }
        }

        // Remove BP refinements on invalid categories since BXS always sends the placeholder
        const refinements = isBpValidCategory
            ? result.withSortRefinements
            : result.withSortRefinements.filter(refinement => refinement.type !== 'beautyPreferences');

        const beautyPreferencesToSave = categorySpecificMasterList
            ? catalogUtils.getBeautyPreferencesSpokeValues({
                userSavedBeautyPreferences: customerPreference,
                constructorRefinements: refinements,
                categorySpecificMasterList
            })
            : {};

        return {
            prevPageId: pageId,
            prevContextId: catalog.contextId,
            selectedFilters: result.filters,
            appliedFilters: result.filters,
            refinements,
            categoryFilters: result.categoryFilters,
            hasClientSideData: catalog?.hasClientSideData,
            trackFilterSelection: false,
            suppressNextAsyncPageLoad: false,
            categorySpecificMasterList,
            prevCustomerPreference: customerPreference,
            beautyPreferencesToSave
        };
    }

    unmountBannersAndPlas() {
        RmnAndPlaUtils.resetPlaSponsorProducts();

        if (this.responsiveCheck) {
            this.responsiveCheck.cleanup();
        }

        RmnAndPlaUtils.resetBanners();
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }

        if (this.unsubscribeZipCode) {
            this.unsubscribeZipCode();
        }

        // GUAR-8295: clear pending delivery-options gate
        clearTimeout(this.deliveryOptionsGateTimeout);

        if (this.resolveDeliveryOptionsGate) {
            this.resolveDeliveryOptionsGate();
            this.resolveDeliveryOptionsGate = null;
        }

        // GUAR-8295: clear the synchronous suppress flag so the next page mount starts clean
        delete digitalData.page.attributes.plpEddGated;

        // GUAR-8295: clear any deferred zip-filter payload that never fired (user navigated
        // away before deliveryDate arrived). Prevents stale pageName/pageType from being sent
        // on a later interaction if ProductGrid re-mounts elsewhere and deliveryOptions arrives.
        delete digitalData.page.attributes.pendingZipFilterAnalytics;

        if (isRmnCombinedCallFeatureEnabled()) {
            this.unmountBannersAndPlas();
        }

        window.removeEventListener(PostLoad, this.initPixlee);
    }

    componentDidMount() {
        const {
            catalog,
            onStoreChangedFromHeader = Empty.Function,
            onZipCodeChangedFromHeader = Empty.Function,
            isStoreIdAndZipCodeReady,
            validateUpperFunnelParams,
            getFulfillmentOptions = Empty.Function
        } = this.props;
        const { categoryFilters } = this.state;

        this.setState({ isBeautyPreferencesSpokeDisabled: Storage.session.getItem(LOCAL_STORAGE.DISABLE_BEAUTY_PREFERENCES_SPOKE) });

        getMatchReferer();

        if (catalog?.categories) {
            CatalogPageBindings.setPageLoadAnalytics(catalog, categoryFilters); // hardload analytics
        }

        // GUAR-8295: If a zip-code refinement is pre-applied on this hard load AND
        // deliveryOptions has not yet arrived in the catalog, gate the initial PAGE_LOAD
        // until deliveryOptions arrives (or 500ms timeout) so event320/eVar141 are
        // included in the first s.t() call rather than a follow-up call.
        // Use this.state.appliedFilters (URL format like 'filters[SameDay]=Yes') —
        // NOT categoryFilters (analytics-display format like 'same day=yes').
        const appliedFilterValues = Object.values(this.state.appliedFilters || {}).flat();
        const hasZipFilterPreApplied = ZIP_CODE_REFINEMENTS.some(key =>
            appliedFilterValues.some(val => typeof val === 'string' && val.startsWith(key))
        );
        const needsDeliveryOptionsGate = hasZipFilterPreApplied && !catalog?.deliveryOptions?.shipToHome?.deliveryDate;

        if (needsDeliveryOptionsGate && Array.isArray(Sephora.analytics?.initialLoadDependencies)) {
            // GUAR-8295: synchronous flag so the suppress check in getDerivedStateFromProps
            // doesn't race with PAGE_LOAD bindings finishing.
            digitalData.page.attributes.plpEddGated = true;

            const gatePromise = new Promise(resolve => {
                this.resolveDeliveryOptionsGate = resolve;
            });

            Sephora.analytics.initialLoadDependencies.push(gatePromise);

            this.deliveryOptionsGateTimeout = setTimeout(() => {
                if (this.resolveDeliveryOptionsGate) {
                    // GUAR-8371: deliveryOptions never arrived — release the gate AND clear
                    // plpEddGated. PAGE_LOAD will fire without event320, and we must not
                    // leave a stale suppress flag that would silently drop a later deferred
                    // ASYNC_PAGE_LOAD when deliveryOptions eventually shows up.
                    delete digitalData.page.attributes.plpEddGated;
                    this.resolveDeliveryOptionsGate();
                    this.resolveDeliveryOptionsGate = null;
                }
            }, 500);
        }

        this.props.dispatchMarketingParams();

        this.unsubscribe = onStoreChangedFromHeader();

        this.unsubscribeZipCode = onZipCodeChangedFromHeader();

        if (isRmnCombinedCallFeatureEnabled()) {
            this.responsiveCheck = new ComponentResponsiveChangeCheck(MOBILE_VIEW, this.requestPageBannersAndPlas);

            // Resets any digitalData sponsor banner information from previous pages.
            RmnAndPlaUtils.resetBanners();
        }

        if (this.isPixleeEnabled()) {
            window.addEventListener(PostLoad, this.initPixlee);
        }

        if (isStoreIdAndZipCodeReady && !catalog?.deliveryOptions) {
            validateUpperFunnelParams(getFulfillmentOptions);
        }

        const categoryName = catalog?.displayName || '';
        fireRoktPageView({ pagetype: 'shop', screenname: categoryName, title: categoryName });
    }

    componentDidUpdate(prevProps) {
        const { sponsoredProducts, totalBasketCount, catalog } = this.props;

        if (catalog?.categoryId && catalog.categoryId !== prevProps.catalog?.categoryId) {
            const categoryName = catalog.displayName || '';
            fireRoktPageView({ pagetype: 'shop', screenname: categoryName, title: categoryName });
        }

        // GUAR-8295: release the PAGE_LOAD gate as soon as catalog.deliveryOptions arrives
        // so the initial s.t() includes event320 + eVar141 from generalBindings.getPageEvents().
        const deliveryDateJustArrived =
            !prevProps.catalog?.deliveryOptions?.shipToHome?.deliveryDate && !!catalog?.deliveryOptions?.shipToHome?.deliveryDate;

        if (deliveryDateJustArrived && this.resolveDeliveryOptionsGate) {
            // GUAR-8371: clear plpEddGated once the gate resolves so the flag stays an
            // initial-load-only signal. handleZipFilterAnalytics's suppress path also
            // clears it, but only when getDerivedStateFromProps hits that branch on
            // this render — clearing here ensures it never lingers past the gate window.
            // (getDerivedStateFromProps runs BEFORE componentDidUpdate in the same render,
            // so the Round-4 suppress still sees plpEddGated=true when it needs to.)
            clearTimeout(this.deliveryOptionsGateTimeout);
            delete digitalData.page.attributes.plpEddGated;
            this.resolveDeliveryOptionsGate();
            this.resolveDeliveryOptionsGate = null;
        }

        if (isRmnCombinedCallFeatureEnabled()) {
            this.updateBannersAndPlas(prevProps);
        }

        // Adding sponsored products information on the digitalData object.
        const sponsorProducts = (sponsoredProducts?.products || []).map(product => {
            return {
                skuId: product?.currentSku?.skuId,
                clickTrackerId: product[CLICK_TRACKER_ID_FIELD] || '',
                impressionTrackerId: product?.impression_id || '',
                impressionPayload: product?.impression_payload || '',
                clickPayload: product?.click_payload || ''
            };
        });
        digitalData.page.category.sponsoredProducts = sponsorProducts;

        if (Sephora.Util?.InflatorComps?.services?.CatalogService) {
            Sephora.Util.InflatorComps.services.CatalogService.catalogEngine = this.props.catalog?.responseSource;
        }

        digitalData.page.attributes.totalBasketCount = totalBasketCount;

        if (this.props.userInfo.isAnonymous && !digitalData.anonymousUserId) {
            digitalData.anonymousUserId = Sephora.Util.getBasketCache()?.profileId;
        }
    }

    updateBannersAndPlas(prevProps) {
        const { catalog } = this.props;

        const targetUrl = catalog?.targetUrl || '';

        if (prevProps.catalog?.targetUrl !== targetUrl) {
            RmnAndPlaUtils.resetPlaSponsorProducts();

            // Resets any digitalData sponsor banner information from previous pages.
            RmnAndPlaUtils.resetBanners();
            this.responsiveCheck.triggerCallback();
        }
    }

    setLoading = loading => {
        this.setState({ bannerLoading: loading });
    };

    requestPageBannersAndPlas = async isMobile => {
        const { catalog } = this.props;
        const targetUrl = catalog.targetUrl || '';
        const { routes } = spaUtils.getSpaTemplateInfoByTemplate(Sephora.template) || {};
        const category = catalogUtils.getCatalogName(targetUrl, routes?.[0]);
        const targets = { category: [category] };

        const productCount = catalog?.products?.length || 0;

        const plaSponsoredProductOpts = {
            targetUrl: catalog?.targetUrl || '',
            products: catalog?.products || Empty.Array,
            categoryId: catalog?.categoryId || ''
        };

        const slot = await RmnAndPlaUtils.handleBannerAndPlaData({
            contextId: catalog.categoryId,
            targets,
            isMobile,
            setLoading: this.setLoading,
            pageType: RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY,
            targetUrl,
            productCount,
            plaSponsoredProductOpts
        });

        const state = store.getState();

        const rmnBanners = state?.rmnBanners || {};

        // Gets the Side Rail Banner
        const bannersSlot = rmnBanners[slot];
        const bannerObjects = bannersSlot?.banners || {};
        const fallbackBanner = bannersSlot?.fallback || {};

        const getBannerPayload = (key, bannerPosition = 0) => {
            const banner = bannerObjects?.[key]?.[bannerPosition] || {};

            if (Object.keys(banner).length > 0) {
                return { ...banner, slot };
            }

            return {};
        };

        this.setState({
            rmnSideRailBannerPayload: getBannerPayload(BANNER_AND_PLA_TYPES.SIDERAIL),
            rmnLeaderboardBannerPayload: getBannerPayload(BANNER_AND_PLA_TYPES.LEADERBOARD, LEADERBOARD_POSITION_INDEX.TOP),
            rmnMidPageBannerPayload: getBannerPayload(BANNER_AND_PLA_TYPES.LEADERBOARD, LEADERBOARD_POSITION_INDEX.MID),
            rmnBottomBannerPayload: getBannerPayload(BANNER_AND_PLA_TYPES.LEADERBOARD, LEADERBOARD_POSITION_INDEX.BOTTOM),
            fallbackBannerPayload: fallbackBanner
        });

        const { getSponsorProducts = Empty.Function, sponsoredProducts } = this.props;

        const sponsoredProductOpts = {
            targetUrl: catalog?.targetUrl || '',
            products: catalog?.products || Empty.Array,
            categoryId: catalog?.categoryId || ''
        };

        if (!sponsoredProducts.error) {
            getSponsorProducts(sponsoredProductOpts);
        }
    };

    shouldUseLiveUpdates = () => {
        return Sephora.configurationSettings.sortModalUpdates?.isEnabled;
    };

    onModalOpen = () => {
        if (this.shouldUseLiveUpdates()) {
            this.setState({
                initialFiltersSnapshot: deepExtend({}, this.state.appliedFilters)
            });
        }
    };

    confirmSelection = () => {
        if (this.shouldUseLiveUpdates()) {
            this.setState({ initialFiltersSnapshot: null });
        }
    };

    selectFilters = (filtersToSelect, applyFilters = false, _ref, options = {}) => {
        const { fromModal = false } = options;
        const newSelectedFilters = catalogUtils.addToSelection(this.state.selectedFilters, filtersToSelect);
        const shouldApplyFilters = applyFilters || this.shouldUseLiveUpdates();
        const sortSelected = filtersToSelect[getCatalogText('sort')]?.length > 0;
        const hasNewFilters = catalogUtils.hasNewFiltersAdded(this.state.selectedFilters, filtersToSelect);
        const trackFilterSelection = !fromModal && shouldApplyFilters && !sortSelected && hasNewFilters;
        this.applySelectionIfChanged(newSelectedFilters, shouldApplyFilters, trackFilterSelection, { fromModal });
    };

    removeFilterValue = (filterKey, filterValue, applyFilters = false) => {
        const newSelectedFilters = catalogUtils.removeValueFromSelection(this.state.selectedFilters, filterKey, filterValue);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters || this.shouldUseLiveUpdates());
    };

    clearFiltersSelection = (applyFilters, resetSortToDefault) => {
        nthCategoryBindings.clearAllChiclets();
        const { selectedFilters } = this.state;
        const newSelectedFilters = catalogUtils.resetSelection(selectedFilters, resetSortToDefault);
        this.applySelectionIfChanged(newSelectedFilters, applyFilters || this.shouldUseLiveUpdates());
    };

    discardSelection = (applyFilters = false) => {
        const { initialFiltersSnapshot } = this.state;

        if (this.shouldUseLiveUpdates() && initialFiltersSnapshot) {
            this.applySelectionIfChanged({ ...initialFiltersSnapshot }, true);
            this.setState({ initialFiltersSnapshot: null });
        } else {
            this.applySelectionIfChanged(this.state.appliedFilters, applyFilters);
        }
    };

    transformFiltersForAnalytics = filters => {
        const formattedFilters = [];

        for (const filterName in filters) {
            if (filters[filterName].length > 0) {
                const formattedFilterName = filterName.toLowerCase();
                const values = filters[filterName].map(value => {
                    // Regular filters have the following format:
                    // filters[Brand]=MILK MAKEUP
                    // but price range has this one
                    // pl=0&ph=250&ptype=manual

                    if (formattedFilterName === 'price range') {
                        return value;
                    } else {
                        const parts = value.split('=');

                        return parts.length > 1 ? parts[1].toLowerCase() : '';
                    }
                });

                formattedFilters.push(`${formattedFilterName}=${values.join(',')}`);
            }
        }

        return formattedFilters.join(';');
    };

    applySelectionIfChanged = (newSelectedFilters, applyFilters = false, trackFilterSelection = false, options = {}) => {
        const { fromModal = false } = options;
        const { selectedFilters: currentSelectedFilters } = this.state;

        if (JSON.stringify(newSelectedFilters) !== JSON.stringify(currentSelectedFilters)) {
            const nextState = { selectedFilters: newSelectedFilters, trackFilterSelection };

            if (applyFilters && this.shouldUseLiveUpdates()) {
                nextState.appliedFilters = { ...newSelectedFilters };
            }

            if (fromModal && applyFilters) {
                nextState.suppressNextAsyncPageLoad = true;
            }

            this.setState(nextState, () => {
                if (applyFilters) {
                    this.applySelectedFilters();

                    if (!fromModal) {
                        const tappedChiclets = this.transformFiltersForAnalytics(newSelectedFilters);
                        nthCategoryBindings.selectFilter({ tappedChiclets });
                    }
                }
            });
        }
    };

    applySelectedFilters = (trackFilterSelection = false) => {
        if (trackFilterSelection) {
            this.setState({ trackFilterSelection: true });
        }

        const { selectedFilters, refinements } = this.state;
        const filtersToApply = catalogUtils.createFiltersToApply(selectedFilters, refinements);
        this.props.applyFilters(filtersToApply);
    };

    getCurrentPageFromCatalog = ({ currentPage }) => {
        return currentPage || 1;
    };

    isPixleeEnabled = () => {
        const { catalog } = this.props;

        // remove `categoryUGCWidget` after successful contentful A/B test - ECOM-1601
        return Sephora.configurationSettings.enableUGCWidget && (catalog.content?.ugcWidget?.widgetId || catalog.categoryUGCWidget);
    };

    initPixlee = () => {
        loadPixlee();
    };

    getPixleeWidgetId = () => {
        const isUSInsider = userUtils.isInsider() && localeUtils.isUS();

        if (this.isPixleeEnabled() && !this.props.userInfo.isAnonymous && !isUSInsider) {
            const { catalog } = this.props;
            // remove `locale` + `categoryUGCWidget` after successful contentful A/B test - ECOM-1601
            const locale = `${localeUtils.getCurrentCountry().toLowerCase()}-${localeUtils.getCurrentLanguage().toLowerCase()}`;

            return catalog.content?.ugcWidget?.widgetId || catalog.categoryUGCWidget?.[locale];
        } else {
            return null;
        }
    };

    isFiltered = () => catalogUtils.isFiltered(this.state.appliedFilters);

    flagRmnBannerViewableImpression = rmnObj => {
        rmnObj['impressionFired'] = true;
    };

    hideBeautyPreferencesSpoke = () => {
        this.setState({ isBeautyPreferencesSpokeDisabled: true, isSpokeKeepAlive: false });
    };

    keepSpokeAlive = () => {
        this.setState({ isSpokeKeepAlive: true });
    };

    resetSpokeKeepAlive = () => {
        this.setState({ isSpokeKeepAlive: false });
    };

    renderRmnLeaderboardBanner = slotPrefix => {
        const { rmnLeaderboardBannerPayload, fallbackBannerPayload, bannerLoading } = this.state;

        return (
            <div>
                <RmnLeaderboardBanner
                    minHeight={134}
                    position={BANNER_POSITIONS.TOP}
                    pageType={RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY}
                    showSkeleton={bannerLoading}
                    rmnBannerPayload={rmnLeaderboardBannerPayload}
                    fallbackBannerPayload={fallbackBannerPayload}
                    bannerProps={{
                        slot:
                            rmnLeaderboardBannerPayload?.slot || `${slotPrefix}${TYPES.SUPER_LEADERBOARD.SLOT[RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY]}`,
                        section: SECTIONS.MAIN,
                        type: TYPES.SUPER_LEADERBOARD.NAME,
                        position: BANNER_POSITIONS.TOP,
                        source: RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY,
                        marginBottom: 4,
                        clickEventName: mountBannerEventData({
                            section: SECTIONS.MAIN,
                            targetPage: anaConsts.RMN_PAGE_NAMES.category,
                            type: anaConsts.EVENTS_TYPES_NAME.CLICK
                        }),
                        viewableEventName: mountBannerEventData({
                            section: SECTIONS.MAIN,
                            targetPage: anaConsts.RMN_PAGE_NAMES.category,
                            type: anaConsts.EVENTS_TYPES_NAME.VIEW
                        })
                    }}
                />
            </div>
        );
    };

    renderRmnBottomBanner = slotPrefix => {
        const { rmnBottomBannerPayload, fallbackBannerPayload } = this.state;

        if (!Object.keys(rmnBottomBannerPayload)?.length) {
            return null;
        }

        return (
            <RmnBottomBanner
                position={BANNER_POSITIONS.BOTTOM}
                pageType={RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY}
                rmnBannerPayload={rmnBottomBannerPayload}
                fallbackBannerPayload={fallbackBannerPayload}
                bannerMobileProps={{
                    slot: rmnBottomBannerPayload?.slot || `${slotPrefix}${TYPES.SUPER_LEADERBOARD.SLOT[RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY]}`,
                    section: SECTIONS.MAIN,
                    type: TYPES.SUPER_LEADERBOARD.NAME,
                    position: BANNER_POSITIONS.BOTTOM,
                    source: RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY,
                    clickEventName: mountBannerEventData({
                        section: SECTIONS.MAIN,
                        targetPage: anaConsts.RMN_PAGE_NAMES.category,
                        type: anaConsts.EVENTS_TYPES_NAME.CLICK
                    }),
                    viewableEventName: mountBannerEventData({
                        section: SECTIONS.MAIN,
                        targetPage: anaConsts.RMN_PAGE_NAMES.category,
                        type: anaConsts.EVENTS_TYPES_NAME.VIEW
                    })
                }}
            />
        );
    };

    // eslint-disable-next-line complexity
    render() {
        const {
            catalog,
            plaEnabled,
            userInfo,
            updateCustomerPreference,
            showMidPageBanner,
            setFilterBarVisibility,
            filterBarHidden = false,
            persistFiltersOnCategory,
            showSimplifyFiltersOnPLP
        } = this.props;
        const {
            categories,
            categoryId,
            contextId,
            linkEquityBlock = {},
            quickFilter,
            pageType,
            parentCategory,
            schemas,
            resultId,
            content
        } = catalog;
        const {
            selectedFilters,
            appliedFilters,
            refinements,
            isPaginationFirstPage,
            bannerLoading,
            isBeautyPreferencesSpokeDisabled,
            categorySpecificMasterList
        } = this.state;

        const seoContent = catalog.seoMetaContent || catalog.seoContent || [];
        const hasSeoContent = seoContent.length > 0 || catalog?.seoText;
        const showBeautyPreferencesSpoke = !isBeautyPreferencesSpokeDisabled && !userInfo?.isAnonymous;
        const categoriesProps = {
            contextId,
            pageType,
            categories,
            parentCategory,
            categoryId,
            shouldPersistFilters: persistFiltersOnCategory
        };

        const filters = (
            <Filters
                pageId={categoryId}
                contextId={contextId}
                selectedFilters={selectedFilters}
                appliedFilters={appliedFilters}
                refinements={refinements}
                selectFilters={this.selectFilters}
                discardSelection={this.discardSelection}
                clearFiltersSelection={this.clearFiltersSelection}
                applySelectedFilters={this.applySelectedFilters}
                removeFilterValue={this.removeFilterValue}
                onModalOpen={this.onModalOpen}
                confirmSelection={this.confirmSelection}
                shouldStickySideBar
                targetShadowScrollY={210}
                userInfo={userInfo}
                categorySpecificMasterList={categorySpecificMasterList}
                setFilterBarVisibility={setFilterBarVisibility}
                filterBarHidden={filterBarHidden}
                categoriesProps={categoriesProps}
                totalProducts={catalog?.totalProducts}
                showCompactPills
                showSimplifyFiltersOnPLP={showSimplifyFiltersOnPLP}
            />
        );

        const breadcrumbProps = {
            key: contextId,
            categories,
            categoryId,
            pageType: 'category'
        };

        const h1SeoName = catalogUtils.getCategoryDisplayName(categories, categoryId);
        const pixleeWidgetId = this.getPixleeWidgetId();
        const targetUrl = catalog.targetUrl || '';
        const { routes } = spaUtils.getSpaTemplateInfoByTemplate(Sephora.template) || {};
        const category = catalogUtils.getCatalogName(targetUrl, routes?.[0]);
        const targets = { category: [category] };
        const slotPrefix = localeUtils.isUS() ? '25' : '26';
        const filtered = this.isFiltered();

        const searchBrowseFilteredPagePLAs = Sephora.configurationSettings?.smn?.searchBrowseFilteredPagePLAs?.isEnabled ?? true;
        const showSponsorProducts = plaEnabled && isPaginationFirstPage && (!filtered || searchBrowseFilteredPagePLAs);
        const allAppliedFiltersAreEmpty = Object.values(appliedFilters).every(filter => filter.length === 0);
        const sideBarSkeletonHeight = allAppliedFiltersAreEmpty
            ? refinements.length * COLLAPSED_ACCORDION_HEIGHT + EXTRA_HEIGHT_WHEN_ALL_FILTERS_SHOWN
            : DEFAULT_SIDEBAR_HEIGHT;
        const mobileSidebarSkeletonHeight = 174;
        const { beautyPreferencesToSave } = this.state;
        const hasBeautyPreferencesToSave = Object.keys(beautyPreferencesToSave).length > 0;

        const tilesGridResultCount = (catalog?.products?.length || 0) + (catalog?.content?.marketingTiles?.length || 0);

        const { rmnSideRailBannerPayload, rmnMidPageBannerPayload, fallbackBannerPayload } = this.state;

        const { carouselOnPLPCategory, carouselOnPLPNthCategory } = this.props;

        // Determine page type
        const isCategoryPage = pageType === PAGE_TYPES.CATEGORY_PAGE;
        const isNthCategory = pageType === PAGE_TYPES.NTH_CATEGORY_PAGE;

        // Use the appropriate AB test variable based on page type
        // carouselOnPLPCategory is for CATEGORY_PAGE (SBR-3467)
        // carouselOnPLPNthCategory is for NTH_CATEGORY_PAGE
        const carouselTest = isCategoryPage ? carouselOnPLPCategory : isNthCategory ? carouselOnPLPNthCategory : null;
        const isCarouselEnabled = carouselTest?.enabled === true;

        // Configure carousel based on page type - all logic in one place
        let carouselPodId = '';
        let excludeListConfig = [];
        let carouselTitle = '';

        if (isCategoryPage) {
            // Category Page: Use dynamic template (SBR-3652)
            carouselPodId = CONSTRUCTOR_PODS.PLP_CATEGORY;
            excludeListConfig = Sephora.configurationSettings.categoryPageTopCarouselExcludeList;
            carouselTitle = `${getCatalogText('trendingIn')} ${h1SeoName}`;
        } else if (isNthCategory) {
            // Nth Category: Use Constructor API title (SBR-3652)
            carouselPodId = CONSTRUCTOR_PODS.PLP_NTH_LEVEL;
            excludeListConfig = Sephora.configurationSettings.nthCategoryPageTopCarouselExcludeList;
            carouselTitle = ''; // Empty - RecServiceCarousel will use API displayName
        }

        // Get root parent category to determine eligibility
        const rootParentCategoryId = catalog?.categories?.[0]?.categoryId;
        const isUnderEligibleCategory = CAROUSEL_ELIGIBLE_ROOT_CATEGORY_IDS.includes(rootParentCategoryId);

        // Check if category is excluded
        const excludedCategories = Array.isArray(excludeListConfig) ? excludeListConfig : [];
        const isExcludedCategory = excludedCategories.includes(categoryId);

        // Hardcode category filter - always filter by current category
        const carouselParams = { filters: [{ key: 'group_id', value: categoryId }] };

        // Show carousel if all conditions are met:
        // - Page type is Category Page or Nth Category (not Top Category)
        // - Feature enabled via AB test
        // - No refinement filters are applied (sorting is not a filter and should not hide the carousel - SBR-3655)
        // - Category is under one of the eligible main categories (Skincare, Makeup, Bath & Body, Hair, Fragrance)
        // - Category is not in exclusion list
        const hasRefinementFilters = catalogUtils.hasActiveRefinementFilters(appliedFilters);
        const showCarousel =
            (isCategoryPage || isNthCategory) && isCarouselEnabled && !hasRefinementFilters && isUnderEligibleCategory && !isExcludedCategory;

        return (
            <div ref={this.layoutWrapRef}>
                {this.props.isOptimizeShippingCopyPLPActive && this.props.userInfo.isAnonymous && (
                    <Media lessThan='md'>
                        <FreeShippingBanner />
                    </Media>
                )}
                <CatalogLayout
                    filterBarHidden={filterBarHidden}
                    content={{
                        top: <BreadCrumbs {...breadcrumbProps} />,
                        sidebar: (
                            <CatalogLayout.SideBar shouldStickySideBar>
                                <Media greaterThan='sm'>
                                    {bannerLoading ? (
                                        <SkeletonBanner height={[null, mobileSidebarSkeletonHeight, sideBarSkeletonHeight]} />
                                    ) : (
                                        <RenderSidebar
                                            catalog={catalog}
                                            categoryId={categoryId}
                                            targets={targets}
                                            pageType={pageType}
                                            categoriesProps={categoriesProps}
                                            slotPrefix={slotPrefix}
                                            filters={filters}
                                            isMobile={false}
                                            flagViewableImpression={this.flagRmnBannerViewableImpression}
                                            rmnObj={{ impressionFired: false }}
                                            rmnBannerPayload={rmnSideRailBannerPayload}
                                            fallbackBannerPayload={fallbackBannerPayload}
                                            rootRef={this.layoutWrapRef}
                                        />
                                    )}
                                </Media>
                                <Media
                                    lessThan='md'
                                    css={{ display: 'contents' }}
                                >
                                    <RenderSidebar
                                        catalog={catalog}
                                        categoryId={categoryId}
                                        targets={targets}
                                        pageType={pageType}
                                        categoriesProps={categoriesProps}
                                        slotPrefix={slotPrefix}
                                        filters={filters}
                                        isMobile={true}
                                        flagViewableImpression={this.flagRmnBannerViewableImpression}
                                        bannerLoading={bannerLoading}
                                        rmnObj={{ impressionFired: false }}
                                        rmnBannerPayload={rmnSideRailBannerPayload}
                                    />
                                </Media>
                            </CatalogLayout.SideBar>
                        ),
                        main: (
                            //Do not make this outer container into an actual element - it will break css ordering of the nested elements
                            <>
                                {this.props.isOptimizeShippingCopyPLPActive && this.props.userInfo.isAnonymous && (
                                    <Media greaterThan='sm'>
                                        <FreeShippingBanner />
                                    </Media>
                                )}
                                {isRmnCombinedCallFeatureEnabled() ? this.renderRmnLeaderboardBanner(slotPrefix) : null}
                                {pageType === PAGE_TYPES.TOP_CATEGORY && (
                                    <SoftLinks
                                        key={contextId}
                                        links={content?.softLinks || quickFilter}
                                        customStyles={{
                                            [mediaQueries.smMax]: {
                                                marginBottom: -space[2],
                                                marginTop: -space[2],
                                                paddingTop: space[2],
                                                paddingBottom: space[2]
                                            },
                                            [mediaQueries.md]: {
                                                marginBottom: space[4]
                                            }
                                        }}
                                    />
                                )}
                                {showCarousel && (
                                    <>
                                        <RecServiceCarousel
                                            podId={carouselPodId}
                                            {...(carouselTitle && { title: carouselTitle })}
                                            {...(carouselTitle && { customTitle: carouselTitle })}
                                            params={carouselParams}
                                            grouping={isCategoryPage ? GROUPING.PLP_CATEGORY : isNthCategory ? GROUPING.PLP_NTH_LEVEL : null}
                                            useSimplifiedCarouselTracking
                                        />
                                        <Divider marginBottom={[4, 5]} />
                                    </>
                                )}
                                {showBeautyPreferencesSpoke && (hasBeautyPreferencesToSave || this.state.isSpokeKeepAlive) ? (
                                    <BeautyPreferenceSpoke
                                        beautyPreferencesToSave={beautyPreferencesToSave}
                                        profileId={userInfo.profileId}
                                        customerPreference={userInfo.customerPreference}
                                        categorySpecificMasterList={categorySpecificMasterList}
                                        updateCustomerPreference={updateCustomerPreference}
                                        onHideSpoke={this.hideBeautyPreferencesSpoke}
                                        onSaveStart={this.keepSpokeAlive}
                                        onSaveComplete={this.resetSpokeKeepAlive}
                                    />
                                ) : null}
                                <ProductGrid
                                    h1={h1SeoName}
                                    layoutWrapRef={this.layoutWrapRef}
                                    selectedFilters={selectedFilters}
                                    appliedFilters={appliedFilters}
                                    refinements={refinements}
                                    selectFilters={this.selectFilters}
                                    source={'category'}
                                    removeFilterValue={this.removeFilterValue}
                                    clearAllFilters={this.clearFiltersSelection}
                                    showSponsorProducts={showSponsorProducts}
                                    showMidPageBanner={showMidPageBanner}
                                    rmnBannerPayload={rmnMidPageBannerPayload}
                                    fallbackBannerPayload={fallbackBannerPayload}
                                    midPageBannerProps={{
                                        slot:
                                            rmnMidPageBannerPayload?.slot ||
                                            `${slotPrefix}${TYPES.SUPER_LEADERBOARD.SLOT[RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY]}`,
                                        section: SECTIONS.MAIN,
                                        type: TYPES.SUPER_LEADERBOARD.NAME,
                                        position: BANNER_POSITIONS.MID,
                                        source: RMN_BANNER_TYPES.PAGE_TYPES.CATEGORY,
                                        clickEventName: mountBannerEventData({
                                            section: SECTIONS.MAIN,
                                            targetPage: anaConsts.RMN_PAGE_NAMES.category,
                                            type: anaConsts.EVENTS_TYPES_NAME.CLICK
                                        }),
                                        viewableEventName: mountBannerEventData({
                                            section: SECTIONS.MAIN,
                                            targetPage: anaConsts.RMN_PAGE_NAMES.category,
                                            type: anaConsts.EVENTS_TYPES_NAME.VIEW
                                        })
                                    }}
                                    categoryId={categoryId}
                                    isBrowse
                                    addItemCnstrcData
                                    showSimplifyFiltersOnPLP={showSimplifyFiltersOnPLP}
                                />
                                {tilesGridResultCount > 12 && (isRmnCombinedCallFeatureEnabled() ? this.renderRmnBottomBanner(slotPrefix) : null)}
                            </>
                        ),
                        bottom: (
                            <>
                                {(content?.faqHeader || content?.faqCollection?.items?.length) && (
                                    <FAQCollection
                                        faqHeader={content.faqHeader}
                                        faqCollection={content.faqCollection}
                                    />
                                )}
                                {pageType === PAGE_TYPES.TOP_CATEGORY || (
                                    <BreadCrumbs
                                        {...breadcrumbProps}
                                        isBottom={true}
                                    />
                                )}
                                {pixleeWidgetId && (
                                    <LazyLoad
                                        component={PixleeContainer}
                                        categoryId={categoryId}
                                        widgetId={pixleeWidgetId}
                                        containerId='PixleeContainer'
                                    />
                                )}
                                <Categories
                                    {...categoriesProps}
                                    isBottom={true}
                                />
                                {content?.copy?.length > 0 && content?.copy?.[0]?.content && (
                                    <>
                                        <Divider
                                            marginTop={[4, 5]}
                                            marginBottom={0}
                                        />
                                        {content?.copy?.map((item, index) => (
                                            <Copy
                                                content={item.content}
                                                key={item.sid}
                                                marginTop={[4, index === 0 ? 5 : 4]}
                                                marginBottom={index === content.copy.length - 1 && hasSeoContent ? [4, 5] : 0}
                                            />
                                        ))}
                                    </>
                                )}
                                {hasSeoContent && (
                                    <Box>
                                        <Divider
                                            marginTop={[4, 5]}
                                            marginBottom={[4, 5]}
                                        />
                                        <SeoText
                                            contextId={categoryId}
                                            content={seoContent}
                                            text={catalog?.seoText}
                                        />
                                    </Box>
                                )}
                                <RelatedContent
                                    hasDivider={hasSeoContent || content?.copy?.length > 0 || categoriesProps?.categories?.length > 0}
                                    links={linkEquityBlock?.links}
                                />
                            </>
                        )
                    }}
                    currentPage={this.getCurrentPageFromCatalog(catalog)}
                    columnWidth={FILTERS_SIDEBAR_WIDTH.STICKY}
                    backToTopPosition={110}
                />

                {schemas?.length &&
                    schemas.map(schema => (
                        <script
                            key={schema['@type']}
                            type='application/ld+json'
                            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
                        ></script>
                    ))}
                {resultId && (
                    <script
                        id='cnstrc-data'
                        type='application/json'
                        dangerouslySetInnerHTML={{ __html: JSON.stringify({ resultId }) }}
                    ></script>
                )}
            </div>
        );
    }
}

NthCategoryPage.propTypes = {
    getFulfillmentOptions: PropTypes.func,
    onStoreChangedFromHeader: PropTypes.func,
    onZipCodeChangedFromHeader: PropTypes.func,
    getSponsorProducts: PropTypes.func,
    isPaginationFirstPage: PropTypes.bool,
    showStickyFilter: PropTypes.bool
};

export default wrapComponent(NthCategoryPage, 'NthCategoryPage', true);
